import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Button } from '../../components/admin/shared/Button';
import { Input } from '../../components/admin/shared/Input';
import { Modal } from '../../components/admin/shared/Modal';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import { Plus, Edit, Trash2 } from 'lucide-react';

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const [groupedSkills, setGroupedSkills] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '' });
  const { toasts, addToast } = useToast();

  const fetchSkills = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('skills')
        .select('*')
        .order('order');

      if (error) throw error;
      setSkills(data || []);

      // Group by category
      const grouped = (data || []).reduce((acc, skill) => {
        const category = skill.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(skill);
        return acc;
      }, {});
      setGroupedSkills(grouped);
    } catch (error) {
      console.error('Error fetching skills:', error);
      addToast('Error loading skills', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleAdd = () => {
    setEditingSkill(null);
    setFormData({ name: '', category: '' });
    setShowModal(true);
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({ name: skill.name, category: skill.category || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingSkill) {
        const { error } = await supabase
          .from('skills')
          .update({ name: formData.name, category: formData.category })
          .eq('id', editingSkill.id);

        if (error) throw error;
        addToast('Skill updated successfully', 'success');
      } else {
        const maxOrder = skills.length > 0 ? Math.max(...skills.map(s => s.order || 0)) : 0;
        const { error } = await supabase
          .from('skills')
          .insert({
            name: formData.name,
            category: formData.category,
            order: maxOrder + 1
          });

        if (error) throw error;
        addToast('Skill added successfully', 'success');
      }
      setShowModal(false);
      fetchSkills();
    } catch (error) {
      console.error('Error saving skill:', error);
      addToast('Error saving skill', 'error');
    }
  };

  const handleDelete = async (skillId) => {
    if (!window.confirm('Delete this skill?')) return;

    try {
      const { error } = await supabase
        .from('skills')
        .delete()
        .eq('id', skillId);

      if (error) throw error;
      addToast('Skill deleted successfully', 'success');
      fetchSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
      addToast('Error deleting skill', 'error');
    }
  };

  const categories = Object.keys(groupedSkills).sort();

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Skills</h1>
        <Button
          variant="primary"
          onClick={handleAdd}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Add Skill
        </Button>
      </div>

      {loading ? (
        <div className="text-white">Loading...</div>
      ) : (
        <div className="space-y-6">
          {categories.map((category) => (
            <div key={category} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold text-white mb-4">{category}</h2>
              <div className="space-y-2">
                {groupedSkills[category].map((skill) => (
                  <div
                    key={skill.id}
                    className="flex items-center justify-between p-3 bg-slate-700 rounded-lg"
                  >
                    <span className="text-white">{skill.name}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(skill)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(skill.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSkill ? 'Edit Skill' : 'Add Skill'}
      >
        <Input
          label="Skill Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., React, Flutter, Python"
        />
        <Input
          label="Category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          placeholder="e.g., Mobile, Web, AI/ML"
        />
        <div className="flex gap-4 mt-6">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editingSkill ? 'Update' : 'Add'} Skill
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Skills;

